import Button from '@material-ui/core/Button';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import PriceFieldPreview from '@sprinx/react-mui-fields/PriceFieldPreview';
import clsx from 'clsx';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { currencyState, showPricesState } from '../../api/appState';
import { shoppingCartState } from '../../api/shoppingCart';
import useRouteUrlFactory from '../../hooks/useRouteUrlFactory';
import useTranslateWithFallback from '../../hooks/useTranslateWithFallback';
import CartBuyForFreeDelivery from '../../pages/CartSummaryPage/CartBuyForFreeDelivery';

export type ShoppingCartAsideTotalsProps = StandardDivProps<ShoppingCartAsideTotalsClassKey>;

export type ShoppingCartAsideTotalsClassKey = 'root' | 'prices' | 'actions' | 'orderButton' | 'deliveryFree';

const themeSettings = { name: 'ShoppingCartAsideTotals' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<ShoppingCartAsideTotalsClassKey, {}>({
      root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderTop: `2px solid ${theme.palette.divider}`,
      },
      prices: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        paddingTop: theme.spacing(2),
        width: 'auto',
      },
      actions: {
        display: 'flex',
        flexDirection: 'column',
        '& button': {
          marginTop: theme.spacing(2),
        },
      },
      orderButton: {
        backgroundColor: '#1D1D1B',
        color: 'white',
        '&:hover': {
          backgroundColor: '#786C3A',
        },
      },
      deliveryFree: {
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
      },
    }),
  themeSettings,
);

const ShoppingCartAsideTotals = React.forwardRef<HTMLDivElement, ShoppingCartAsideTotalsProps>(
  ({ className, classes: pClasses }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const currency = useRecoilValue(currencyState);
    const cart = useRecoilValue(shoppingCartState);
    const showPrices = useRecoilValue(showPricesState);
    const routeUrlFactory = useRouteUrlFactory();
    const t = useTranslateWithFallback();

    return (
      <div ref={ref} className={clsx(classes.root, className)}>
        {showPrices && (
          <div className={classes.prices}>
            <PriceFieldPreview
              label={t('common/totalWithoutTax')}
              value={cart?.cartContent.totalSubjects || 0}
              currency={currency}
              fullWidth
              align='right'
              alignLabel
              size='large'
            />
            {cart?.cartContent.total != cart?.cartContent.totalWithTax && (
              <PriceFieldPreview
                label={t('common/totalWithTax')}
                value={cart?.cartContent.totalSubjectsWithTax || 0}
                currency={currency}
                fullWidth
                align='right'
                alignLabel
              />
            )}
          </div>
        )}
        <div className={classes.actions}>
          <Button
            component={RouterLink}
            to={routeUrlFactory('cartSummary')}
            className={classes.orderButton}
            size='large'
            variant='contained'
          >
            {t('shoppingCart/order')}
          </Button>
          {showPrices && <CartBuyForFreeDelivery className={classes.deliveryFree} />}
        </div>
      </div>
    );
  },
);

ShoppingCartAsideTotals.displayName = 'ShoppingCartAsideTotals';

export default withThemeProps(themeSettings)(ShoppingCartAsideTotals);
